import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

/**
 * `<VinChip vin="…" />` — monospace VIN text + tap-to-copy affordance.
 *
 * Used wherever a full VIN is displayed (vehicle detail spec, vehicle
 * card lists, customer detail vehicle row, TaskEric search-result
 * vehicle tile). The chip itself is the copy button — tapping anywhere
 * on the VIN copies it; no separate icon-only "copy" button to hunt for
 * on mobile.
 *
 * For the appointment-tile use case where the surrounding tile is
 * itself a click target (drag-to-reschedule, click-to-edit), keep using
 * the icon-only `CopyVinButton` — it's tuned for a tight tile layout.
 *
 * Three states:
 *   - idle   — VIN in monospace + small copy icon, hover highlights
 *   - copied — Check icon + brief "Copied" toast for 1.2s
 *
 * Browsers can reject clipboard writes (insecure context / permission).
 * On failure we render the VIN as plain text — it's still selectable
 * by hand.
 */
export function VinChip({
  vin,
  className,
  size = 'sm',
}: {
  vin: string
  className?: string
  /** `sm` = list-row default. `xs` = denser tile / column rendering. */
  size?: 'xs' | 'sm'
}) {
  const { t } = useTranslation('common')
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [])

  async function handleCopy(e: React.MouseEvent | React.KeyboardEvent) {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(vin)
      setCopied(true)
      if (timerRef.current) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => setCopied(false), 1200)
    } catch {
      // No-op — VIN is still on-screen for manual selection.
    }
  }

  const padding = size === 'xs' ? 'px-1.5 py-0.5' : 'px-2 py-1'
  const textSize = size === 'xs' ? 'text-[10px]' : 'text-xs'
  const iconSize = size === 'xs' ? 'size-3' : 'size-3.5'
  const label = copied ? t('vin.copied') : t('vin.copy', { vin })

  return (
    <>
      <button
        type="button"
        onClick={handleCopy}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleCopy(e)
        }}
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex max-w-full items-center gap-1.5 rounded font-mono transition',
          padding,
          textSize,
          copied
            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
            : 'bg-slate-100 text-slate-800 ring-1 ring-inset ring-slate-200 hover:bg-slate-200/70 hover:text-slate-900',
          className,
        )}
      >
        <span className="min-w-0 truncate">{vin}</span>
        {copied ? (
          <Check className={cn(iconSize, 'shrink-0')} aria-hidden />
        ) : (
          <Copy className={cn(iconSize, 'shrink-0 opacity-60')} aria-hidden />
        )}
      </button>
      {copied ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white shadow-xl"
        >
          {t('vin.copied_toast')}
        </div>
      ) : null}
    </>
  )
}
